import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Link as LinkIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import PageHeader from "../../components/PageHeader";
import StyledDataGrid from "../../components/StyledDataGrid";
import WorkspacePage from "../../components/WorkspacePage";
import useOptions from "../../hooks/useOptions";
import {
  createConsentFormGroup,
  fetchConsentFormGroups,
  fetchStudies,
  saveConsentFormGroup,
} from "../../utils/api";

const maroon = "#500000";

const EMPTY_ENTRY = {
  consentFormId: "",
  language: "",
  widgetUrl: "",
  duration: "",
  durationUnit: "",
};

const DEFAULT_STUDY_DURATION = "5";
const DEFAULT_STUDY_DURATION_UNIT = "YEAR";

const outlineButtonSx = {
  borderColor: maroon,
  color: maroon,
  textTransform: "none",
  fontWeight: 600,
  "&:hover": {
    borderColor: maroon,
    backgroundColor: "rgba(80, 0, 0, 0.08)",
  },
};

const ConsentFormManagement = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [draft, setDraft] = useState({
    consentName: "",
    consentType: "STUDY",
    irbStudy: "",
    entries: [{ ...EMPTY_ENTRY }],
    deletedConsentFormIds: [],
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const {
    options: languageOptions,
    loading: languagesLoading,
    error: languagesError,
  } = useOptions("enums/Language");
  const {
    options: durationUnitOptions,
    loading: durationUnitsLoading,
    error: durationUnitsError,
  } = useOptions("enums/DurationUnit");

  const studyOptions = useMemo(
    () =>
      (Array.isArray(studies) ? studies : []).map((study) => ({
        value: study.irbStudy,
        label: `${study.irbStudy}: ${study.studyName || study.irbStudy}`,
      })),
    [studies]
  );

  const rows = useMemo(
    () =>
      (Array.isArray(groups) ? groups : []).map((group) => ({
        id: `${group.consentName || "general"}-${group.irbStudy || "general"}`,
        consentName: group.consentName || "",
        consentType: group.consentType || "STUDY",
        irbStudy: group.irbStudy || "",
        studyDisplayName: group.studyDisplayName || "",
        entries: group.entries || [],
        source: group,
      })),
    [groups]
  );

  const loadData = async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const [groupData, studyData] = await Promise.all([
        fetchConsentFormGroups(),
        fetchStudies(),
      ]);
      setGroups(Array.isArray(groupData) ? groupData : []);
      setStudies(Array.isArray(studyData) ? studyData : []);
    } catch (error) {
      console.error("Error loading consent form management data:", error);
      setErrorMessage(error.message || "Failed to load consent forms.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetDialog = () => {
    setDialogOpen(false);
    setEditingKey(null);
    setFormErrors({});
    setDraft({
      consentName: "",
      consentType: "STUDY",
      irbStudy: "",
      entries: [{ ...EMPTY_ENTRY }],
      deletedConsentFormIds: [],
    });
  };

  const handleCreate = () => {
    setEditingKey(null);
    setDraft({
      consentName: "",
      consentType: "STUDY",
      irbStudy: "",
      entries: [
        {
          ...EMPTY_ENTRY,
          language: languageOptions[0]?.value || "",
          duration: DEFAULT_STUDY_DURATION,
          durationUnit: DEFAULT_STUDY_DURATION_UNIT,
        },
      ],
      deletedConsentFormIds: [],
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleEdit = (group) => {
    setEditingKey(group.consentName);
    setDraft({
      consentName: group.consentName || "",
      consentType: group.consentType || "STUDY",
      irbStudy: group.irbStudy || "",
      entries:
        Array.isArray(group.entries) && group.entries.length > 0
          ? group.entries.map((entry) => ({
              consentFormId: entry.consentFormId || "",
              language: entry.language || "",
              widgetUrl: entry.widgetUrl || "",
              duration:
                group.consentType === "STUDY" ? entry.duration ?? "" : "",
              durationUnit:
                group.consentType === "STUDY" ? entry.durationUnit || "" : "",
            }))
          : [{ ...EMPTY_ENTRY }],
      deletedConsentFormIds: [],
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const updateEntry = (index, field, value) => {
    setDraft((prev) => ({
      ...prev,
      entries: prev.entries.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [field]: value } : entry
      ),
    }));
  };

  const addEntry = () => {
    setDraft((prev) => ({
      ...prev,
      entries: [
        ...prev.entries,
        {
          ...EMPTY_ENTRY,
          language:
            languageOptions.find(
              (option) =>
                !prev.entries.some((entry) => entry.language === option.value)
            )?.value || "",
          duration:
            prev.consentType === "STUDY" ? DEFAULT_STUDY_DURATION : "",
          durationUnit:
            prev.consentType === "STUDY" ? DEFAULT_STUDY_DURATION_UNIT : "",
        },
      ],
    }));
  };

  const removeEntry = (index) => {
    setDraft((prev) => {
      const removed = prev.entries[index];
      const nextDeletedIds = removed?.consentFormId
        ? [...prev.deletedConsentFormIds, removed.consentFormId]
        : prev.deletedConsentFormIds;
      const nextEntries = prev.entries.filter(
        (_, entryIndex) => entryIndex !== index
      );

      return {
        ...prev,
        entries: nextEntries.length > 0 ? nextEntries : [{ ...EMPTY_ENTRY }],
        deletedConsentFormIds: nextDeletedIds,
      };
    });
  };

  const validate = () => {
    const errors = {};

    if (!draft.consentName.trim()) {
      errors.consentName = "Consent name is required.";
    }
    if (!draft.consentType) {
      errors.consentType = "Consent type is required.";
    }
    if (draft.consentType === "STUDY" && !draft.irbStudy) {
      errors.irbStudy = "Study is required for study consent forms.";
    }
    if (!draft.entries.length) {
      errors.entries = "At least one language entry is required.";
    }

    const languages = new Set();
    draft.entries.forEach((entry, index) => {
      if (!entry.language) {
        errors[`entry-language-${index}`] = "Language is required.";
      } else if (languages.has(entry.language)) {
        errors[`entry-language-${index}`] =
          "Duplicate language is not allowed.";
      } else {
        languages.add(entry.language);
      }

      if (!entry.widgetUrl.trim()) {
        errors[`entry-widgetUrl-${index}`] = "Widget URL is required.";
      }

      const durationValue = `${entry.duration ?? ""}`.trim();
      if (draft.consentType !== "STUDY") {
        return;
      }
      if (draft.consentType === "STUDY" && !durationValue) {
        errors[`entry-duration-${index}`] =
          "Duration is required for study consent forms.";
      }
      if (
        durationValue &&
        (!Number.isInteger(Number(durationValue)) || Number(durationValue) <= 0)
      ) {
        errors[`entry-duration-${index}`] =
          "Duration must be a positive whole number.";
      }
      if (durationValue && !entry.durationUnit) {
        errors[`entry-durationUnit-${index}`] =
          "Duration unit is required when duration is set.";
      }
      if (!durationValue && entry.durationUnit) {
        errors[`entry-duration-${index}`] =
          "Duration is required when duration unit is set.";
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (languagesLoading || languagesError) {
      setErrorMessage("Language options must load before saving.");
      return;
    }
    if (
      draft.consentType === "STUDY" &&
      (durationUnitsLoading || durationUnitsError)
    ) {
      setErrorMessage("Duration unit options must load before saving.");
      return;
    }
    if (!validate()) {
      return;
    }

    const payload = {
      consentName: draft.consentName.trim(),
      consentType: draft.consentType,
      irbStudy: draft.consentType === "STUDY" ? draft.irbStudy : null,
      entries: draft.entries.map((entry) => {
        const durationValue =
          draft.consentType === "STUDY" ? `${entry.duration ?? ""}`.trim() : "";
        return {
          consentFormId: entry.consentFormId || null,
          language: entry.language,
          widgetUrl: entry.widgetUrl.trim(),
          duration: durationValue ? Number(durationValue) : null,
          durationUnit: durationValue ? entry.durationUnit : null,
        };
      }),
      deletedConsentFormIds: draft.deletedConsentFormIds,
    };

    try {
      setSaving(true);
      if (editingKey) {
        await saveConsentFormGroup(payload);
      } else {
        await createConsentFormGroup(payload);
      }

      setSnackbar({
        open: true,
        message: editingKey
          ? "Consent form group updated successfully."
          : "Consent form group created successfully.",
        severity: "success",
      });
      await loadData();
      resetDialog();
    } catch (error) {
      console.error("Error saving grouped consent form:", error);
      setErrorMessage(error.message || "Failed to save consent form group.");
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      field: "consentName",
      headerName: "Consent Name",
      minWidth: 260,
      flex: 1.1,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ py: 1.5 }}>
          <Typography sx={{ color: maroon, fontWeight: 700 }}>
            {params.row.consentName || "N/A"}
          </Typography>
          <Typography variant="caption" sx={{ color: "#666" }}>
            {params.row.consentType === "GENERAL"
              ? "General consent"
              : params.row.studyDisplayName || params.row.irbStudy || "Study consent"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "entries",
      headerName: "Widget Links",
      minWidth: 400,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Stack spacing={1} sx={{ py: 1.5, minWidth: 0, overflow: "hidden" }}>
          {(params.value || []).map((entry) => (
            <Box
              key={`${params.row.id}-${entry.language}-link`}
              sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {entry.language || "N/A"}:
              </Typography>
              {entry.widgetUrl ? (
                <Box sx={{ minWidth: 0, overflow: "hidden" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0, overflow: "hidden" }}>
                    <LinkIcon sx={{ fontSize: "1rem", color: maroon, flexShrink: 0 }} />
                    <Typography
                      component="a"
                      href={entry.widgetUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{
                        color: maroon,
                        textDecoration: "none",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {entry.widgetUrl}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: "#666" }}>
                    {entry.duration && entry.durationUnit
                      ? `${entry.duration} ${entry.durationUnit}`
                      : "No expiration"}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: "#999" }}>
                  No URL
                </Typography>
              )}
            </Box>
          ))}
        </Stack>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} sx={{ py: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleEdit(params.row.source)}
            sx={{ ...outlineButtonSx, borderRadius: "20px", px: 2 }}
          >
            Edit
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <WorkspacePage sx={{ p: 3 }}>
      <Box sx={{ mb: 2 }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/admin")}
          sx={{ color: "#666", fontWeight: 600, textTransform: "none", p: 0, "&:hover": { backgroundColor: "transparent", color: maroon } }}
        >
          Back to Dashboard
        </Button>
      </Box>
      <PageHeader
        title="Consent Form Management"
        actions={
          <Button
            variant="contained"
            onClick={handleCreate}
            sx={{
              backgroundColor: maroon,
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { backgroundColor: "#4a0000" },
            }}
          >
            Create Consent Form
          </Button>
        }
      />

      <Box sx={{ mb: 2 }}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      </Box>

      <Card sx={{ flex: 1, minHeight: 0, borderRadius: 0, boxShadow: "none" }}>
        <CardContent sx={{ p: 0, height: "100%" }}>
          <StyledDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            getRowHeight={() => "auto"}
            sx={{
              height: "100%",
              borderColor: "#d8d8d8",
              "& .MuiDataGrid-cell": {
                alignItems: "flex-start",
                justifyContent: "flex-start",
                textAlign: "left",
                py: 0.5,
              },
              "& .MuiDataGrid-columnHeaderTitleContainer": {
                justifyContent: "flex-start",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: maroon,
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#faf5f5",
              },
            }}
          />
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={resetDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: maroon, color: "white" }}>
          {editingKey ? "Edit Consent Form" : "Create New Consent Form"}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 1 }}>
            <FormControl fullWidth error={!!formErrors.consentType}>
              <InputLabel>Consent Type</InputLabel>
              <Select
                value={draft.consentType}
                onChange={(event) => {
                  const nextConsentType = event.target.value;
                  setDraft((prev) => ({
                    ...prev,
                    consentType: nextConsentType,
                    irbStudy: nextConsentType === "GENERAL" ? "" : prev.irbStudy,
                    entries:
                      nextConsentType === "STUDY"
                        ? prev.entries.map((entry) => ({
                            ...entry,
                            duration: entry.duration || DEFAULT_STUDY_DURATION,
                            durationUnit:
                              entry.durationUnit || DEFAULT_STUDY_DURATION_UNIT,
                          }))
                        : prev.entries.map((entry) => ({
                            ...entry,
                            duration: "",
                            durationUnit: "",
                          })),
                  }));
                }}
                label="Consent Type"
              >
                <MenuItem value="STUDY">Study</MenuItem>
                <MenuItem value="GENERAL">General</MenuItem>
              </Select>
            </FormControl>

            {draft.consentType === "STUDY" && (
              <FormControl fullWidth error={!!formErrors.irbStudy}>
                <InputLabel>Study</InputLabel>
                <Select
                  value={draft.irbStudy}
                  onChange={(event) =>
                    setDraft((prev) => ({ ...prev, irbStudy: event.target.value }))
                  }
                  label="Study"
                >
                  {studyOptions.map((study) => (
                    <MenuItem key={study.value} value={study.value}>
                      {study.label}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.irbStudy && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {formErrors.irbStudy}
                  </Typography>
                )}
              </FormControl>
            )}

            <TextField
              fullWidth
              label="Consent Name"
              value={draft.consentName}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, consentName: event.target.value }))
              }
              error={!!formErrors.consentName}
              helperText={formErrors.consentName || "Required"}
            />

            <Box>
              <Typography sx={{ fontWeight: 700, color: maroon, mb: 2 }}>
                Language Variants
              </Typography>
              <Stack spacing={2}>
                {draft.entries.map((entry, index) => (
                  <Box
                    key={entry.consentFormId || `entry-${index}`}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      p: 2,
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        md:
                          draft.consentType === "STUDY"
                            ? "160px 1fr 120px 160px auto"
                            : "160px 1fr auto",
                      },
                      gap: 2,
                      alignItems: "start",
                    }}
                  >
                    <FormControl
                      fullWidth
                      error={!!formErrors[`entry-language-${index}`]}
                      disabled={languagesLoading || !!languagesError}
                    >
                      <InputLabel>Language</InputLabel>
                      <Select
                        value={entry.language}
                        label="Language"
                        onChange={(event) =>
                          updateEntry(index, "language", event.target.value)
                        }
                      >
                        {languageOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {formErrors[`entry-language-${index}`] && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                          {formErrors[`entry-language-${index}`]}
                        </Typography>
                      )}
                    </FormControl>

                    <TextField
                      fullWidth
                      label="Widget URL"
                      value={entry.widgetUrl}
                      onChange={(event) =>
                        updateEntry(index, "widgetUrl", event.target.value)
                      }
                      error={!!formErrors[`entry-widgetUrl-${index}`]}
                      helperText={
                        formErrors[`entry-widgetUrl-${index}`] ||
                        "Adobe Sign widget URL"
                      }
                    />

                    {draft.consentType === "STUDY" && (
                      <>
                        <TextField
                          fullWidth
                          label="Duration"
                          type="number"
                          value={entry.duration}
                          onChange={(event) =>
                            updateEntry(index, "duration", event.target.value)
                          }
                          inputProps={{ min: 1 }}
                          error={!!formErrors[`entry-duration-${index}`]}
                          helperText={
                            formErrors[`entry-duration-${index}`] || "Required"
                          }
                        />

                        <FormControl
                          fullWidth
                          error={!!formErrors[`entry-durationUnit-${index}`]}
                          disabled={durationUnitsLoading || !!durationUnitsError}
                        >
                          <InputLabel>Duration Unit</InputLabel>
                          <Select
                            value={entry.durationUnit}
                            label="Duration Unit"
                            onChange={(event) =>
                              updateEntry(index, "durationUnit", event.target.value)
                            }
                          >
                            {durationUnitOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          {formErrors[`entry-durationUnit-${index}`] && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                              {formErrors[`entry-durationUnit-${index}`]}
                            </Typography>
                          )}
                        </FormControl>
                      </>
                    )}

                    <Box sx={{ display: "flex", justifyContent: "center", pt: 1 }}>
                      <IconButton
                        onClick={() => removeEntry(index)}
                        disabled={draft.entries.length === 1}
                        sx={{ color: "#d32f2f" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Stack>

              <Button
                sx={{ mt: 2, color: maroon, textTransform: "none", fontWeight: 600 }}
                startIcon={<AddIcon />}
                onClick={addEntry}
              >
                Add Language Variant
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={resetDialog} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              saving ||
              languagesLoading ||
              (draft.consentType === "STUDY" && durationUnitsLoading)
            }
            sx={{
              backgroundColor: maroon,
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { backgroundColor: "#4a0000" },
            }}
          >
            {saving ? "Saving..." : editingKey ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </WorkspacePage>
  );
};

export default ConsentFormManagement;
